/**
 * Main layout script class
 *
 * @package    Layouts
 * @author     Vadym Karpenko <vkarpenko@mindk.com>
 * @copyright  Copyright (c) 2010-2018 MindK (http://mindk.com). All rights reserved.
 * @license    http://mindk.com
 * @link       http://mindk.com
 */

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Route, Switch, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { animateScroll } from 'react-scroll';
import ReduxToastr from 'react-redux-toastr';

import './scss/styles.css';

import { authorize, unAuthorize, keepAlive } from 'Actions/auth';
import { KEEP_ALIVE_TIME } from 'Constants';
import AdminLayout from 'Layouts/AdminLayout';
import { Agree, Disagree } from 'Layouts/Invitations';
import Login from 'Layouts/Login';
import Register from 'Layouts/Register';
import NotFound from 'Layouts/NotFound';
import Forbidden from 'Layouts/Forbidden';
import InternalError from 'Layouts/InternalError';
import ResetPassword from 'Layouts/ResetPassword';
import RecoverPassword from 'Layouts/RecoverPassword';
import VerifyEmail from 'Layouts/VerifyEmail';
import { AboutTheSystem, Contacts, FAQ, PrivacyPolicy } from 'Layouts/StaticPages';

@withRouter
@connect(store => ({
    isLoggedIn: store.auth.isLoggedIn,
}), dispatch => ({
    authorize: (data) => dispatch(authorize(data)),
    unAuthorize: () => dispatch(unAuthorize()),
    keepAlive: () => dispatch(keepAlive()),
}))
export default class Layout extends PureComponent {
    /**
     * @type {Object}
     */
    static propTypes = {
        history: PropTypes.any,
        location: PropTypes.any,
        isLoggedIn: PropTypes.bool.isRequired,
        authorize: PropTypes.func.isRequired,
        unAuthorize: PropTypes.func.isRequired,
        keepAlive: PropTypes.func.isRequired,
    };

    componentDidMount() {
        this.unlistenHistoryChanges = this.props.history.listen((location) => {
            if (location.pathname !== this.props.location.pathname) {
                animateScroll.scrollToTop();
            }
        });
        this.keepAlive();
        window.addEventListener('storage', this.storageHandler);
    }

    componentWillUnmount() {
        this.unlistenHistoryChanges();
        clearInterval(this.keepAliveInterval);
        window.removeEventListener('storage', this.storageHandler);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.isLoggedIn !== this.props.isLoggedIn) {
            this.keepAlive();
        }
    }

    storageHandler = (event) => {
        const { newValue, key } = event;
        const { isLoggedIn, authorize, unAuthorize } = this.props;
        if (key === 'auth') {
            let auth = JSON.parse(newValue);
            if (isLoggedIn && !auth) {
                unAuthorize();
            }
            if (!isLoggedIn && auth) {
                authorize(auth);
            }
        }
    };

    keepAlive = () => {
        if (this.props.isLoggedIn) {
            clearInterval(this.keepAliveInterval);
            this.keepAliveInterval = setInterval(this.props.keepAlive, KEEP_ALIVE_TIME);
        } else {
            clearInterval(this.keepAliveInterval);
        }
    };

    render() {
        const { location } = this.props;

        return [
            <Switch
                key="layout-switch"
                location={ location }
            >
                <Route
                    exact
                    path="/login"
                    component={ Login }
                />
                <Route
                    exact
                    path="/recover"
                    component={ RecoverPassword }
                />
                <Route
                    path="/reset/:token"
                    component={ ResetPassword }
                />
                <Route
                    exact
                    path="/register"
                    component={ Register }
                />
                <Route
                    path="/verify/:token"
                    component={ VerifyEmail }
                />
                <Route
                    exact
                    path="/500"
                    component={ InternalError }
                />
                <Route
                    exact
                    path="/404"
                    component={ NotFound }
                />
                <Route
                    exact
                    path="/403"
                    component={ Forbidden }
                />
                <Route
                    exact
                    path="/invitations/agree/:token"
                    component={ Agree }
                />
                <Route
                    exact
                    path="/invitations/disagree/:token"
                    component={ Disagree }
                />
                <Route
                    exact
                    path="/privacy-policy"
                    component={ PrivacyPolicy }
                />
                <Route
                    exact
                    path="/contacts"
                    component={ Contacts }
                />
                <Route
                    exact
                    path="/about-the-system"
                    component={ AboutTheSystem }
                />
                <Route
                    exact
                    path="/faq"
                    component={ FAQ }
                />
                <Route
                    path="/"
                    component={ AdminLayout }
                />
                <Route
                    path="*"
                    component={ NotFound }
                />
            </Switch>,
            <ReduxToastr
                key="layout-notify"
                position="top-center"
                transitionIn="fadeIn"
                transitionOut="fadeOut"
                progressBar
            />,
        ];
    }
}



// WEBPACK FOOTER //
// ./src/layouts/Layout/Layout.jsx